import { useStore } from "stores/store";
import axios from "axios";
import { List } from "models/list";
import { logout } from "stores/actions/auth";

const API_URL = import.meta.env.VITE_PUBLIC_API_URL;

const getUrl = (path: string) => [API_URL, path].join("");

axios.interceptors.request.use(
  (config) => {
    // attach subdomain info as a header
    config.headers.Tenant = window.location.hostname.split(".")[0];

    // inject access token to requests
    const state = useStore.getState();
    const { accessToken } = state;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// intercept response, refresh token if needed and redirect to login if nothing else helps
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // If the error status is 401 and there is no originalRequest._retry flag,
    // it means the access token has expired and we need to refresh it
    const isRefreshRequest = originalRequest.url.includes("/refresh");
    const isLoginRequest = originalRequest.url.includes("/login");
    const is401 = error.response.status === 401;

    // Handle error in refresh request (means the refresh tokene expired and we need to logout)
    if (isRefreshRequest) {
      // make a state logout, the active UI guard should handle the redirect
      return logout();
    }

    // in case of 401, ask for new access token (unless a login related request)
    if (is401 && !isLoginRequest) {
      // check url
      const url = getUrl("/refresh");
      const response = await axios.post(url, {
        refreshToken: useStore.getState().refreshToken,
      });

      // store the new token
      const { refreshToken, accessToken } = response.data;
      useStore.setState((state) => ({
        ...state,
        accessToken,
        refreshToken,
      }));

      // retry the original request with the new token
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return axios(originalRequest);
    }

    return Promise.reject(error);
  },
);

interface RequestOptions {
  // always mandatory
  path: string;

  // optionals
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  headers?: Record<string, string>;

  // json body
  data?: any;
}

// wrapper for ajax requests
const request = async (options: RequestOptions) => {
  const { method = "GET" } = options;
  const url = getUrl(options.path);

  // make request
  const response = await axios({
    method: method.toLowerCase(),
    url,
    data: options.data,
    headers: {
      "Content-Type": "application/json",
    },
  });

  return response.data;
};

// get resource/resources
export const doGet = async <T>(path: string): Promise<T> =>
  request({ method: "GET", path });

// create resource
export const doPost = async <T>(path: string, data?: any): Promise<T> =>
  request({ method: "POST", path, data });

// delete resource
export const doDelete = async (path: string): Promise<void> =>
  request({ method: "DELETE", path });

// update resource
export const doPatch = async <T>(path: string, data?: any): Promise<T> =>
  request({ method: "PATCH", path, data });

export const parseListPath = (list: List, path?: string) => {
  if (path && path[0] === "/") {
    throw new Error("Path must not begin with /");
  }

  if (list.template) {
    return `/admin/settings/list-template/${list.uuid}${path ? `/${path}` : ""}`;
  }
  return `/admin/list/${list.uuid}${path ? `/${path}` : ""}`;
};
